// client/src/services/api.ts
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { 
  API_ENDPOINTS,
  CompleteSignupRequest,
  FormApplication,
  CompleteSignupResponse,
  FormStatus,
  UploadFileResponse
} from 'form-application-shared';

class ApiService {
  private client: AxiosInstance;
  
  constructor() {
    this.client = axios.create({
      baseURL: window.location.origin,
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    // Simple error handling interceptor
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('API request failed:', error);
        return Promise.reject(error);
      }
    );
  }
  
  // Protected to allow access from subclasses
  protected async request<T>(config: AxiosRequestConfig): Promise<T> {
    const response = await this.client(config);
    return response.data;
  }
}

// Form API service with strongly typed methods
export class FormApiService extends ApiService {  
  /**
   * Get form by ID
   */
  async getForm(id: string): Promise<FormApplication> {
    return this.request<FormApplication>({
      method: 'GET',
      url: API_ENDPOINTS.FORM_BY_ID(id)
    });
  }
  
  /**
   * Update form data
   */
  async updateForm(id: string, data: Partial<FormApplication['data']>): Promise<FormApplication> {
    return this.request<FormApplication>({
      method: 'PUT',
      url: API_ENDPOINTS.FORM_BY_ID(id),
      data: { data }
    });
  }
  
  /**
   * Upload a file for ID verification
   */
  async uploadFile(id: string, fieldName: string, file: File): Promise<UploadFileResponse> {
    const formData = new FormData();
    formData.append('file', file);
    
    return this.request({
      method: 'POST',
      url: API_ENDPOINTS.FORM_UPLOAD(id),
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }
  
  /**
   * Complete the signup process
   */
  async completeSignup(id: string): Promise<CompleteSignupResponse> {
    const request: CompleteSignupRequest = { id };
    
    return this.request<CompleteSignupResponse>({
      method: 'POST',
      url: API_ENDPOINTS.FORM_COMPLETE(id),
      data: request
    });
  }
  
  /**
   * Get form status (can be used for polling)
   */
  async checkFormStatus(id: string): Promise<{ status: FormStatus }> {
    return this.request({
      method: 'GET',
      url: `${API_ENDPOINTS.FORM_BY_ID(id)}/status`
    });
  }
  
  /**
   * Duplicate an existing form application
   */
  async duplicateForm(id: string): Promise<string> {
    return this.request<string>({
      method: 'POST',
      url: API_ENDPOINTS.FORM_DUPLICATE(id)
    });
  }
}

// Signing API service for handling signing links and DocuSign integration
export class SigningApiService extends ApiService {
  /**
   * Validate a signing token
   */
  async validateSigningToken(formId: string, token: string): Promise<{
    valid: boolean;
    formId: string;
    signedByEmail: string;
    signedByName: string;
  }> {
    return this.request({
      method: 'GET',
      url: `/api/forms/${formId}/validate-token/${token}`
    });
  }

  /**
   * Generate a DocuSign signing URL using a valid token
   */
  async generateSigningUrl(formId: string, token: string): Promise<{
    signingUrl: string;
  }> {
    return this.request({
      method: 'POST',
      url: `/api/forms/${formId}/generate-signing-url/${token}`
    });
  }

  /**
   * Send a signing link for a completed form (admin function)
   */
  async sendSigningLink(formId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    const token = localStorage.getItem('adminToken');
    return this.request({
      method: 'POST',
      url: `/api/forms/admin/${formId}/send-signing-link`,
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  }

  /**
   * Set the DocuSign envelope ID for a form (admin function)
   */
  async setDocusignEnvelopeId(formId: string, envelopeId: string): Promise<{
    success: boolean;
    message: string;
    envelopeId: string;
  }> {
    const token = localStorage.getItem('adminToken');
    return this.request({
      method: 'POST',
      url: `/api/forms/admin/${formId}/set-envelope-id`,
      data: { envelopeId },
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  }

  /**
   * Revert a completed form back to in-progress status (admin function)
   */
  async revertToInProgress(formId: string): Promise<{
    success: boolean;
    message: string;
    status: string;
  }> {
    const token = localStorage.getItem('adminToken');
    return this.request({
      method: 'POST',
      url: `/api/forms/admin/${formId}/revert-to-in-progress`,
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  }
}

// Create and export singleton instances
export const formApi = new FormApiService();
export const signingApi = new SigningApiService();